import { showNotification } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { X } from 'tabler-icons-react';
import AdminEntityManager from '../../components/admin-entities/admin-entity-manager/admin-entity-manager.component';
import { Entity } from '../../enums/entity.enums';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import useDidMountEffect from '../../hooks/useDidMountEffect';
import { IPaginationMeta } from '../../type/admin.types';
import {
  BasicPartner,
  RetrieveAllEntityRequest,
  RetrieveAllPartnersResponse,
} from '../../type/entity.types';
import { ErrorResponse } from '../../type/error-response.types';

function AdminPartnersPage() {
  const [partnersData, setPartnersData] = useState<BasicPartner[]>();
  const [paginationData, setPaginationData] = useState<IPaginationMeta>();
  const axiosPrivate = useAxiosPrivate();
  const [loadPartnersParams, setLoadPartnersParams] =
    useState<RetrieveAllEntityRequest>({
      page: 1,
      limit: 20,
    });
  const [refreshTable, setRefreshTable] = useState(false);
  const [checkedAll, setCheckedAll] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadPartners = async () => {
    let params: LoadPartnersParams = {
      ...loadPartnersParams,
    };

    //rename search entity to its correct name
    //delete if search was removed
    delete params.searchEntity;
    if (loadPartnersParams.searchEntity?.trim()) {
      params.partnerName = loadPartnersParams.searchEntity;
    } else {
      delete params.partnerName;
    }

    try {
      setIsLoading(true);
      const response = await axiosPrivate.get('/web/partners', {
        params,
      });

      const data: RetrieveAllPartnersResponse = response.data;

      const partners: BasicPartner[] = data?.items;

      setPaginationData(data?.metadata);
      return partners;
    } catch (err: any) {
      const error: ErrorResponse = err.response?.data;

      showNotification({
        id: 'failed-to-load-partners-data',
        message:
          error.error || err?.response?.data || 'An Unexpected Error Occurred',
        icon: <X size={16} />,
        color: 'red',
        autoClose: 10000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const paginationRequest = (page: number, limit: number) => {
    setLoadPartnersParams((prev) => {
      return {
        ...prev,
        page,
        limit,
      };
    });
  };

  const loadAndSetPartners = async () => {
    const partners = await loadPartners();
    setPartnersData(partners);
  };

  //When params change we clear the checks
  useDidMountEffect(() => {
    loadAndSetPartners();
    setCheckedAll(false);
    setIsChecked([]);
  }, [loadPartnersParams]);

  //When params don't change but we need to refresh
  useDidMountEffect(() => {
    loadAndSetPartners();
  }, [refreshTable]);

  useEffect(() => {
    loadAndSetPartners();
  }, []);

  return (
    <>
      <AdminEntityManager
        entityType={Entity.PARTNER}
        partnersData={partnersData}
        paginationRequest={paginationRequest}
        paginationData={paginationData}
        entityParams={loadPartnersParams}
        setEntityParams={setLoadPartnersParams}
        setRefreshTable={setRefreshTable}
        checkedAll={checkedAll}
        setCheckedAll={setCheckedAll}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        isLoading={isLoading}
      />
    </>
  );
}

export default AdminPartnersPage;

interface LoadPartnersParams extends RetrieveAllEntityRequest {
  partnerName?: string;
}
